import type { DailyDepartment } from '@felix/domain';
import { Button, Menu, Skeleton, TextInput, UnstyledButton } from '@mantine/core';
import {
  IconCalendarEvent,
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconClipboardList,
  IconDots,
  IconFileExport,
  IconFilePlus,
  IconHistory,
  IconPlayerPlay,
  IconPlus,
  IconSearch,
  IconSquarePlus,
  IconTool,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { StatusBadge } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { browserPdfOutput, type PdfOutput, renderPdf } from '../personnel/pdf.js';
import { VoyageHeadCard } from '../voyage/VoyageHeadCard.js';
import classes from './Daily.module.css';
import { emptyTask, filterTasks, planTabs, priorityTone, taskBadge, taskInputOf } from './logic.js';
import {
  ConfirmDialog,
  RecentReports,
  ReportsChart,
  SummaryCard,
  timeRange,
  useErrorText,
} from './parts.js';
import { dailyPlanPdf, dailyPlanPdfFileName } from './plan-pdf.js';
import { TaskDetails, TaskDialog } from './TaskDialogs.js';
import type { DailyPlanData, DailySource, DailyTask, DailyTaskInput } from './types.js';

export const DAILY_PLAN_PATH = '/operations/daily-plan';
export const DAILY_REPORTS_PATH = '/operations/daily-reports';

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: DailyPlanData };

/** Gün kaydırma: `YYYY-MM-DD` ± gün. */
export function shiftDay(day: string, delta: number): string {
  return new Date(Date.parse(`${day}T00:00:00.000Z`) + delta * 86_400_000)
    .toISOString()
    .slice(0, 10);
}

/**
 * Operations ▸ Daily Works Plan (BGM `gemi-gunluk-is-plani.jpeg`; ADR 0051 §9): gemi başlık kartı ve
 * tarih, departman sekmeli görev tablosu, sağda gün gezgini, "Today's Summary" ve "Quick Actions",
 * altta son raporlar ve son 7 günün tür grafiği. Gemide yazılır; ofiste salt okunur.
 */
export function DailyPlanScreen({
  source,
  pdf,
  now = () => new Date(),
}: {
  source: DailySource;
  pdf?: PdfOutput | undefined;
  now?: () => Date;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`daily.plan.${key}`, options ?? {});
  const shell = useShell();
  const errorText = useErrorText();
  const query = useMemo(() => new URLSearchParams(shell?.search ?? ''), [shell?.search]);
  const [day, setDay] = useState<string | null>(query.get('day'));
  const [vesselId, setVesselId] = useState<string | null>(query.get('vessel'));
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [reloads, setReloads] = useState(0);
  const [tab, setTab] = useState<DailyDepartment | 'all'>('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<{ task: DailyTask | null; input: DailyTaskInput } | null>(
    null,
  );
  const [workOrders, setWorkOrders] = useState<{ id: string; no: string; title: string }[]>([]);
  const [selected, setSelected] = useState<DailyTask | null>(null);
  const [action, setAction] = useState<{
    kind: 'complete' | 'cancel' | 'remove';
    task: DailyTask;
  } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const editor = source.scope === 'ship' ? source.editor : undefined;

  const reload = useCallback(() => setReloads((n) => n + 1), []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: hata metni dil değişince yenilenmez; yükleme girdilere bağlı
  useEffect(() => {
    let alive = true;
    setLoad((current) => (current.kind === 'ready' ? current : { kind: 'loading' }));
    source
      .plan({ vesselId, day })
      .then((data) => {
        if (!alive) return;
        setLoad({ kind: 'ready', data });
        setSelected((current) => data.tasks.find((task) => task.id === current?.id) ?? null);
      })
      .catch(
        (failure: unknown) => alive && setLoad({ kind: 'error', message: errorText(failure) }),
      );
    return () => {
      alive = false;
    };
  }, [source, vesselId, day, reloads]);

  const go = (path: string) => shell?.navigate(path);
  const data = load.kind === 'ready' ? load.data : null;
  const currentDay = data?.day ?? day;

  const openNew = () => {
    if (!data || !editor) return;
    if (workOrders.length === 0)
      editor
        .workOrders()
        .then(setWorkOrders)
        .catch(() => undefined);
    setEditing({ task: null, input: emptyTask(data.day, tab === 'all' ? 'deck' : tab) });
  };
  const openEdit = (task: DailyTask) => {
    if (!editor) return;
    if (workOrders.length === 0)
      editor
        .workOrders()
        .then(setWorkOrders)
        .catch(() => undefined);
    setEditing({ task, input: taskInputOf(task) });
  };
  const run = (work: Promise<void>) =>
    work.then(reload).catch((failure: unknown) => setNotice(errorText(failure)));

  const exportPdf = () => {
    if (!data) return;
    const output = pdf ?? browserPdfOutput();
    const render = () =>
      renderPdf(
        dailyPlanPdf(
          data,
          (key, options) => t(key, options ?? {}),
          now().toISOString().slice(0, 16),
        ),
      );
    output
      .save(dailyPlanPdfFileName(data.vessel.name, data.day), render)
      .catch((failure: unknown) => setNotice(errorText(failure)));
  };

  const header = (
    <PageHeader
      title={t('menu.operationsDailyPlan')}
      titleHidden
      description={source.scope === 'ship' ? k('description') : k('descriptionOffice')}
      helpKey="operationsDailyPlan"
    />
  );

  if (load.kind === 'loading') {
    return (
      <div className={classes.screen}>
        {header}
        <div className={classes.planGrid} aria-busy="true">
          <Skeleton height={120} radius="md" />
          <Skeleton height={120} radius="md" />
          <Skeleton height={420} radius="md" />
          <Skeleton height={420} radius="md" />
        </div>
      </div>
    );
  }
  if (load.kind === 'error' || !data) {
    return (
      <div className={classes.screen}>
        {header}
        <EmptyState
          tone="danger"
          title={k('loadError')}
          description={load.kind === 'error' ? load.message : undefined}
          action={<Button onClick={reload}>{k('retry')}</Button>}
        />
      </div>
    );
  }

  const tabs = planTabs(data.tasks);
  const visible = filterTasks(data.tasks, { department: tab, query: search });
  const canEdit = Boolean(editor) && data.can.edit;
  const vessel = {
    id: data.vessel.id,
    name: data.vessel.name,
    imo: data.vessel.imo,
  };
  const options = data.vessels?.map((v) => ({ id: v.id, name: v.name, imo: null }));

  return (
    <div className={classes.screen}>
      {header}
      <div className={classes.planGrid}>
        <div className={classes.planMain}>
          <VoyageHeadCard
            label={k('title')}
            vessel={vessel}
            facts={[
              { label: t('daily.head.vesselType'), value: data.vessel.vesselType ?? '—' },
              { label: t('daily.head.flag'), value: data.vessel.flag ?? '—' },
            ]}
            vesselOptions={options && options.length > 1 ? options : undefined}
            onSelectVessel={options ? (id) => setVesselId(id) : undefined}
            aside={
              <DayInput
                className={classes.headDate}
                label={t('daily.head.date')}
                value={data.day}
                onChange={(value) => value && setDay(value)}
              />
            }
          />
          <section className={classes.card} aria-labelledby="daily-plan-title">
            <h2 id="daily-plan-title" className={classes.cardTitle}>
              {k('title')}
            </h2>
            <div className={classes.toolbar}>
              <div className={classes.tabs} role="tablist" aria-label={k('tabs')}>
                {(['all', ...tabs] as const).map((d) => (
                  <UnstyledButton
                    key={d}
                    role="tab"
                    aria-selected={tab === d}
                    className={classes.tab}
                    data-active={tab === d || undefined}
                    onClick={() => setTab(d)}
                  >
                    {d === 'all' ? k('all') : t(`daily.departments.${d}`)}
                  </UnstyledButton>
                ))}
              </div>
              <TextInput
                className={classes.search}
                leftSection={<IconSearch size={16} aria-hidden />}
                placeholder={k('search')}
                aria-label={k('searchLabel')}
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
              />
            </div>
            {notice ? (
              <p className={classes.error} role="alert">
                {notice}
                <UnstyledButton
                  className={classes.iconButton}
                  onClick={() => setNotice(null)}
                  aria-label={t('daily.details.close')}
                >
                  <IconX size={14} />
                </UnstyledButton>
              </p>
            ) : null}
            {data.tasks.length === 0 ? (
              <EmptyState
                icon={<IconCalendarEvent size={28} />}
                title={k('emptyTitle')}
                description={editor ? k('emptyText') : k('emptyOffice')}
                action={
                  editor && data.can.create ? (
                    <Button leftSection={<IconPlus size={16} />} onClick={openNew}>
                      {k('newPlan')}
                    </Button>
                  ) : undefined
                }
              />
            ) : visible.length === 0 ? (
              <p className={classes.muted}>{k('noMatch')}</p>
            ) : (
              <div className={classes.tableWrap}>
                <table className={classes.table} data-variant="plan">
                  <thead>
                    <tr>
                      <th scope="col" data-col="no">
                        {k('columns.no')}
                      </th>
                      <th scope="col">{k('columns.description')}</th>
                      <th scope="col">{k('columns.department')}</th>
                      <th scope="col">{k('columns.priority')}</th>
                      <th scope="col">{k('columns.time')}</th>
                      <th scope="col">{k('columns.responsible')}</th>
                      <th scope="col">{k('columns.status')}</th>
                      <th scope="col" data-center>
                        {k('columns.action')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((task) => {
                      const badge = taskBadge(task);
                      const closed = task.status === 'completed' || task.status === 'cancelled';
                      return (
                        <tr key={task.id} data-closed={task.status === 'cancelled' || undefined}>
                          <td data-col="no">{data.tasks.indexOf(task) + 1}</td>
                          <td>
                            <button
                              type="button"
                              className={classes.taskCell}
                              onClick={() => setSelected(task)}
                            >
                              <span className={classes.taskIcon} aria-hidden>
                                {task.workOrder ? (
                                  <IconTool size={15} />
                                ) : (
                                  <IconClipboardList size={15} />
                                )}
                              </span>
                              <span className={classes.taskText}>{task.description}</span>
                            </button>
                          </td>
                          <td>{t(`daily.departments.${task.department}`)}</td>
                          <td>
                            <StatusBadge tone={priorityTone(task.priority)} withIcon={false}>
                              {t(`daily.priorities.${task.priority}`)}
                            </StatusBadge>
                          </td>
                          <td className={classes.nowrap}>
                            {timeRange(task.startTime, task.endTime)}
                          </td>
                          <td className={classes.wrapCell}>{task.responsible}</td>
                          <td>
                            <StatusBadge tone={badge.tone} withIcon={false}>
                              {t(`daily.statuses.${badge.key}`)}
                            </StatusBadge>
                          </td>
                          <td data-center>
                            <Menu position="bottom-end" withinPortal>
                              <Menu.Target>
                                <UnstyledButton
                                  className={classes.iconButton}
                                  aria-label={k('actionsFor', { task: task.description })}
                                >
                                  <IconDots size={18} />
                                </UnstyledButton>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item onClick={() => setSelected(task)}>{k('view')}</Menu.Item>
                                {canEdit && !closed ? (
                                  <>
                                    <Menu.Item onClick={() => openEdit(task)}>
                                      {k('edit')}
                                    </Menu.Item>
                                    {task.status === 'planned' ? (
                                      <Menu.Item
                                        leftSection={<IconPlayerPlay size={14} />}
                                        onClick={() =>
                                          run(
                                            editor?.changeStatus(task.id, task.rowVersion, {
                                              to: 'in_progress',
                                            }) ?? Promise.resolve(),
                                          )
                                        }
                                      >
                                        {k('start')}
                                      </Menu.Item>
                                    ) : null}
                                    <Menu.Item
                                      leftSection={<IconCheck size={14} />}
                                      onClick={() => setAction({ kind: 'complete', task })}
                                    >
                                      {k('complete')}
                                    </Menu.Item>
                                    {data.can.void ? (
                                      <Menu.Item
                                        onClick={() => setAction({ kind: 'cancel', task })}
                                      >
                                        {k('cancel')}
                                      </Menu.Item>
                                    ) : null}
                                    {data.can.create && task.status === 'planned' ? (
                                      <Menu.Item
                                        color="red"
                                        leftSection={<IconTrash size={14} />}
                                        onClick={() => setAction({ kind: 'remove', task })}
                                      >
                                        {k('remove')}
                                      </Menu.Item>
                                    ) : null}
                                  </>
                                ) : null}
                              </Menu.Dropdown>
                            </Menu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
        <aside className={classes.planSide}>
          <div className={classes.card} data-compact>
            <div className={classes.dayNav}>
              <UnstyledButton
                className={classes.iconButton}
                onClick={() => setDay(shiftDay(data.day, -1))}
                aria-label={k('previousDay')}
              >
                <IconChevronLeft size={18} />
              </UnstyledButton>
              <strong className={classes.dayValue}>{formatDay(currentDay)}</strong>
              <UnstyledButton
                className={classes.iconButton}
                onClick={() => setDay(shiftDay(data.day, 1))}
                aria-label={k('nextDay')}
              >
                <IconChevronRight size={18} />
              </UnstyledButton>
              {editor && data.can.create ? (
                <Button leftSection={<IconPlus size={16} />} onClick={openNew}>
                  {k('newPlan')}
                </Button>
              ) : null}
            </div>
          </div>
          <SummaryCard data={data} />
          <section className={classes.card} aria-labelledby="daily-quick-title">
            <h2 id="daily-quick-title" className={classes.cardTitle}>
              {t('daily.quick.title')}
            </h2>
            <div className={classes.quick}>
              {editor && data.can.create ? (
                <UnstyledButton className={classes.quickItem} onClick={openNew}>
                  <IconSquarePlus size={18} aria-hidden />
                  {t('daily.quick.addWork')}
                </UnstyledButton>
              ) : null}
              {editor && data.can.report ? (
                <UnstyledButton
                  className={classes.quickItem}
                  onClick={() => go(`${DAILY_REPORTS_PATH}?new=daily`)}
                >
                  <IconFilePlus size={18} aria-hidden />
                  {t('daily.quick.generateReport')}
                </UnstyledButton>
              ) : null}
              <UnstyledButton
                className={classes.quickItem}
                onClick={() =>
                  go(
                    `${DAILY_REPORTS_PATH}?from=${shiftDay(data.day, -6)}&to=${data.day}${vesselId ? `&vessel=${vesselId}` : ''}`,
                  )
                }
              >
                <IconHistory size={18} aria-hidden />
                {t('daily.quick.history')}
              </UnstyledButton>
              <UnstyledButton className={classes.quickItem} onClick={exportPdf}>
                <IconFileExport size={18} aria-hidden />
                {t('daily.quick.exportPdf')}
              </UnstyledButton>
            </div>
          </section>
        </aside>
        <div className={classes.planBottom}>
          <RecentReports
            items={data.recent}
            names={data.names}
            onViewAll={() =>
              go(
                `${DAILY_REPORTS_PATH}?from=${shiftDay(data.day, -6)}&to=${data.day}${vesselId ? `&vessel=${vesselId}` : ''}`,
              )
            }
            onOpen={(item) =>
              go(
                `${DAILY_REPORTS_PATH}?from=${item.reportDay}&to=${item.reportDay}&report=${item.id}${vesselId ? `&vessel=${vesselId}` : ''}`,
              )
            }
          />
          <ReportsChart chart={data.chart} />
        </div>
      </div>
      <TaskDetails
        task={selected}
        tzOffsetMin={data.tzOffsetMin}
        onClose={() => setSelected(null)}
        onOpenWorkOrder={(id) =>
          go(
            `/pms/work-orders?wo=${id}${source.scope === 'web' ? `&vessel=${data.vessel.id}` : ''}`,
          )
        }
      />
      {editor && editing ? (
        <TaskDialog
          opened
          initial={editing.input}
          editing={editing.task !== null}
          workOrders={workOrders}
          onClose={() => setEditing(null)}
          onSave={async (input) => {
            if (editing.task)
              await editor.updateTask(editing.task.id, editing.task.rowVersion, input);
            else await editor.createTask(input);
            if (input.planDay !== data.day) setDay(input.planDay);
            reload();
          }}
        />
      ) : null}
      {editor && action ? (
        <ConfirmDialog
          opened
          title={t(`daily.task.${action.kind}Title`)}
          text={
            action.kind === 'remove'
              ? t('daily.task.removeText', { task: action.task.description })
              : t(`daily.task.${action.kind}Text`)
          }
          reasonLabel={
            action.kind === 'complete'
              ? t('daily.task.note')
              : action.kind === 'cancel'
                ? t('daily.task.reason')
                : undefined
          }
          reasonRequired={action.kind !== 'remove'}
          danger={action.kind !== 'complete'}
          confirmLabel={t(`daily.task.${action.kind}Confirm`)}
          onClose={() => setAction(null)}
          onConfirm={async ({ reason }) => {
            const { task } = action;
            if (action.kind === 'remove') await editor.removeTask(task.id, task.rowVersion);
            else if (action.kind === 'complete')
              await editor.changeStatus(task.id, task.rowVersion, {
                to: 'completed',
                note: reason,
              });
            else await editor.changeStatus(task.id, task.rowVersion, { to: 'cancelled', reason });
            reload();
          }}
        />
      ) : null}
    </div>
  );
}
